import React, { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { EmailDialog } from './EmailDialog'
import { useSessionStore } from '../stores/useSessionStore'
import { useApplicationStore } from '../stores/useApplicationStore'
import {
  Settings,
  User,
  KeyRound,
  LogOut,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronDown,
  Building2,
  Palette,
  Users,
  Calendar,
  BarChart3,
  Eye,
  LifeBuoy,
  Wrench,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { cn } from '@/lib/utils'
import {
  WelcomeBanner,
  WelcomeBannerTrigger,
} from './common/WelcomeBanner'

const CHANGE_PASSWORD_URL =
  import.meta.env.VITE_APP_CHANGE_PASSWORD_REDIRECT || ''

const navLinks = [
  { to: '/client', label: 'Clients', icon: Building2 },
  { to: '/identity', label: 'Identities', icon: Users },
  { to: '/event', label: 'Events', icon: Calendar },
  { to: '/report', label: 'Reports', icon: BarChart3 },
]

interface Props {
  children: React.ReactNode
}

export const ApplicationTemplate: React.FC<Props> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const location = useLocation()

  const { hasAdmin, hasScheduleAccessForAnyone, logout, session } =
    useSessionStore()
  const latestToken = useSessionStore((s) => s.latestToken)
  const endMasquerade = useSessionStore((s) => s.endMasquerade)
  const { refreshClients, refreshRegions, refreshEvents } = useApplicationStore()

  // Derive masquerade directly from the JWT to bypass any persist/rehydration races
  // around session.masquerade. The token is the source of truth.
  const masquerade = useMemo(() => {
    if (!latestToken) return null
    try {
      const decoded = jwtDecode<any>(latestToken)
      if (decoded?.actualRequesterId) {
        return {
          actualRequesterId: decoded.actualRequesterId as number,
          actualRequesterName:
            (decoded.actualRequesterName as string | undefined) || 'admin',
        }
      }
    } catch {
      /* not a valid token — fall through */
    }
    return null
  }, [latestToken])

  // Load core data on app initialization so it's available on all pages
  useEffect(() => {
    refreshClients().catch(() => {})
    refreshRegions().catch(() => {})
    refreshEvents().catch(() => {})
  }, [])

  const isActive = (path: string) => location.pathname.startsWith(path)

  return (
    <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-14 items-center px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 mr-8">
            <img
              src="/AG-Logo-No-Barcode.png"
              alt="AuthentiGATE"
              className="h-7 w-auto"
            />
            {/* <span className="hidden lg:inline-block text-sm font-semibold text-foreground/80 tracking-tight">
              EMS
            </span> */}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {navLinks.map((link) => {
              const Icon = link.icon
              const active = isActive(link.to)
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {link.label}
                </Link>
              )
            })}
          </nav>

          {/* Desktop Right */}
          <div className="hidden md:flex items-center gap-1">
            <WelcomeBannerTrigger className="h-9 w-9" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/region">
                    <MapPin className="h-4 w-4 mr-2" />
                    Work Regions
                  </Link>
                </DropdownMenuItem>
                {hasAdmin() && (
                  <DropdownMenuItem asChild>
                    <Link to="/identity/import">
                      <Upload className="h-4 w-4 mr-2" />
                      Import Identities
                    </Link>
                  </DropdownMenuItem>
                )}
                {hasAdmin() && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/badge-templates">
                      <Palette className="h-4 w-4 mr-2" />
                      Badge Templates
                    </Link>
                  </DropdownMenuItem>
                )}
                {hasAdmin() && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/support-tickets">
                      <LifeBuoy className="h-4 w-4 mr-2" />
                      Support Tickets
                    </Link>
                  </DropdownMenuItem>
                )}
                {hasAdmin() && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/support-recipients">
                      <Mail className="h-4 w-4 mr-2" />
                      Support Recipients
                    </Link>
                  </DropdownMenuItem>
                )}
                {hasAdmin() && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/maintenance">
                      <Wrench className="h-4 w-4 mr-2" />
                      Maintenance
                    </Link>
                  </DropdownMenuItem>
                )}
                {(hasAdmin() || hasScheduleAccessForAnyone()) && (
                  <DropdownMenuItem onClick={() => setEmailDialogOpen(true)}>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 gap-2 px-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                    {(session.firstName?.[0] || '').toUpperCase()}
                    {(session.lastName?.[0] || '').toUpperCase()}
                  </div>
                  <span className="text-sm font-medium hidden lg:inline">
                    {session.firstName} {session.lastName}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 lg:hidden">
                  <p className="text-sm font-medium">
                    {session.firstName} {session.lastName}
                  </p>
                </div>
                <DropdownMenuSeparator className="lg:hidden" />
                {session.id && (
                  <DropdownMenuItem asChild>
                    <Link to={`/identity/${session.id}`}>
                      <User className="h-4 w-4 mr-2" />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <a
                    href={CHANGE_PASSWORD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <KeyRound className="h-4 w-4 mr-2" />
                    Reset Password
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Toggle */}
          <div className="md:hidden ml-auto">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t bg-background">
            <nav className="container mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => {
                const Icon = link.icon
                const active = isActive(link.to)
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      active
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </Link>
                )
              })}
              <div className="border-t pt-2 mt-2 space-y-1">
                <Link
                  to="/region"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md"
                >
                  <MapPin className="h-4 w-4" />
                  Work Regions
                </Link>
                {session.id && (
                  <Link
                    to={`/identity/${session.id}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md"
                  >
                    <User className="h-4 w-4" />
                    My Profile
                  </Link>
                )}
                <button
                  onClick={() => {
                    setMobileOpen(false)
                    logout()
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-destructive hover:bg-muted/50 rounded-md w-full text-left"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="pt-14">
        <WelcomeBanner />
        {masquerade && (
          <div className="bg-amber-100 border-b border-amber-300 px-4 py-2">
            <div className="container mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-amber-900">
                <Eye className="h-4 w-4 shrink-0" />
                <span>
                  Masquerading as{' '}
                  <span className="font-semibold">
                    {session.firstName} {session.lastName}
                  </span>{' '}
                  &mdash; admin{' '}
                  <span className="font-semibold">
                    {masquerade.actualRequesterName}
                  </span>
                  . Every action is audited.
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 bg-white hover:bg-amber-50"
                onClick={async () => {
                  try {
                    await endMasquerade()
                  } catch (error) {
                    toast.error(extractErrorMessage(error, 'Failed to exit masquerade'))
                  }
                }}
              >
                Exit masquerade
              </Button>
            </div>
          </div>
        )}
        <EmailDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} />
        {children}
      </main>
    </div>
  )
}
